import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Phone, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { callLogService } from "@/services/callLogService";
import { useToast } from "@/components/ui/use-toast";
import { ScheduleLeadCallDialog } from "../leads/ScheduleLeadCallDialog";
import { ConfirmCallDialog } from "./ConfirmCallDialog";
import { featureTemplateService } from "@/services/featureTemplateService";
import { campaignService } from "@/services/campaignService";
import { PhoneNumberDisplay } from "@/components/ui/phone-number-display";

interface CampaignCallLogsProps {
  campaignId: string;
  organizationId: string;
}

interface LeadCallData {
  leadId: string;
  name: string;
  email?: string;
  phoneNumber?: string;
  callsCount: number;
  totalDuration: number;
  lastCallStatus: string;
  lastCallOutcome?: string;
  lastCallEndedReason?: string;
  calls: any[];
}

export function CampaignCallLogs({
  campaignId,
  organizationId,
}: CampaignCallLogsProps) {
  const [callLogsByLead, setCallLogsByLead] = useState<LeadCallData[]>([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [confirmCallDialogOpen, setConfirmCallDialogOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedLeadName, setSelectedLeadName] = useState("");
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const [agentName, setAgentName] = useState("");
  const [callVariables, setCallVariables] = useState<Record<string, any>>({});
  const { toast } = useToast();

  useEffect(() => {
    fetchCallLogs();
  }, [campaignId]);

  const fetchCallLogs = async () => {
    if (!campaignId) return;

    setLoading(true);
    try {
      // Fetch call logs for this campaign
      const response = await callLogService.getCallLogs({
        campaignId,
        organizationId,
      });

      // Group call logs by lead
      const logsByLead = new Map<string, any[]>();

      if (response.data) {
        response.data.forEach((log) => {
          if (log.lead_id) {
            if (!logsByLead.has(log.lead_id)) {
              logsByLead.set(log.lead_id, []);
            }
            logsByLead.get(log.lead_id)?.push(log);
          }
        });
      }

      // Process each lead's call logs
      const processedData: LeadCallData[] = [];

      for (const [leadId, calls] of logsByLead.entries()) {
        // Sort calls by date (newest first)
        calls.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        const lastCall = calls[0];
        const lead = lastCall.lead;

        // Debug log to see what data we're getting
        console.log("Last call data:", lastCall);
        console.log("Lead data:", lead);
        console.log("Call phone_number field:", lastCall.phone_number);
        console.log("Call to_number:", lastCall.to_number);
        console.log("Call from_number:", lastCall.from_number);

        // Calculate total duration
        const totalDuration = calls.reduce((sum, call) => {
          const duration = call.duration || 0;
          return sum + duration;
        }, 0);

        // Get phone number from call log or lead
        // For outbound calls, the to_number is the lead's phone number
        const phoneNumber = lastCall.to_number || 
                          lastCall.from_number || 
                          lastCall.caller_number ||
                          lastCall.called_number ||
                          lead?.phone_number ||
                          lastCall.phone_number?.phone_number || // Access phone_number from nested phone_numbers table
                          lastCall.phone_number; // Try the phone_number field directly

        console.log("Extracted phone number:", phoneNumber);
        console.log("Phone number object:", lastCall.phone_number);
        console.log("Caller number:", lastCall.caller_number);
        console.log("Called number:", lastCall.called_number);

        processedData.push({
          leadId,
          name: lead
            ? `${lead.first_name || ""} ${lead.last_name || ""}`.trim()
            : "Unknown",
          email: lead?.email,
          phoneNumber: phoneNumber,
          callsCount: calls.length,
          totalDuration,
          lastCallStatus: lastCall.status || "Unknown",
          lastCallOutcome: lastCall.outcome || lastCall.notes || "",
          lastCallEndedReason: lastCall.ended_reason || "",
          calls,
        });
      }

      setCallLogsByLead(processedData);
    } catch (error) {
      console.error("Error fetching call logs:", error);
      toast({
        title: "Error",
        description: "Failed to load call logs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCall = async (
    leadId: string,
    phoneNumber?: string,
    leadName?: string,
  ) => {
    if (!phoneNumber) {
      toast({
        title: "Error",
        description: "No phone number available for this lead",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get campaign details to find the associated agent
      const { data: campaign } = await campaignService.getCampaign(campaignId);
      if (!campaign) {
        throw new Error("Campaign not found");
      }

      // Get feature template to get agent details
      const featureTemplate = await featureTemplateService.getFeatureTemplate(
        campaign.feature_template_id,
      );
      if (!featureTemplate) {
        throw new Error("Feature template not found");
      }

      // Set the selected lead and agent information
      setSelectedLeadId(leadId);
      setSelectedLeadName(leadName || "Unknown Lead");
      setSelectedPhoneNumber(phoneNumber);
      setAgentName(featureTemplate.assistant.name);

      // Set call variables from campaign configuration
      setCallVariables(campaign.configuration_data || {});

      // Open the confirmation dialog
      setConfirmCallDialogOpen(true);
    } catch (error) {
      console.error("Error preparing call:", error);
      toast({
        title: "Error",
        description: "Failed to prepare call",
        variant: "destructive",
      });
    }
  };

  const initiateCall = async () => {
    try {
      // Implement actual call initiation logic here
      toast({
        title: "Initiating call",
        description: "Connecting to lead...",
      });

      // This would typically call your call initiation service
      // For example: await callService.initiateCall(selectedLeadId, selectedPhoneNumber, campaignId, callVariables);

      // For now, we'll just simulate a successful call initiation
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast({
        title: "Call initiated",
        description: "Call has been successfully connected",
      });
    } catch (error) {
      console.error("Error initiating call:", error);
      toast({
        title: "Error",
        description: "Failed to initiate call",
        variant: "destructive",
      });
      throw error; // Re-throw to be handled by the dialog
    }
  };

  const handleSchedule = (leadId: string) => {
    setSelectedLeadId(leadId);
    setScheduleDialogOpen(true);
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return <Badge variant="success">Completed</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "no-answer":
      case "no answer":
        return <Badge variant="warning">No Answer</Badge>;
      case "busy":
        return <Badge variant="warning">Busy</Badge>;
      case "canceled":
        return <Badge variant="secondary">Canceled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="py-8 text-center">Loading call logs...</div>;
  }

  if (callLogsByLead.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">
          No call logs found for this campaign
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone Number</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Calls Count</TableHead>
              <TableHead>Call Duration</TableHead>
              <TableHead>Call Status</TableHead>
              <TableHead>Last Call Outcome</TableHead>
              <TableHead>Ended Reason</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {callLogsByLead.map((leadData) => (
              <TableRow key={leadData.leadId}>
                <TableCell className="font-medium">{leadData.name}</TableCell>
                <TableCell>
                  {leadData.phoneNumber ? (
                    <PhoneNumberDisplay phoneNumber={leadData.phoneNumber} />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>{leadData.email || "-"}</TableCell>
                <TableCell>{leadData.callsCount}</TableCell>
                <TableCell>{formatDuration(leadData.totalDuration)}</TableCell>
                <TableCell>{getStatusBadge(leadData.lastCallStatus)}</TableCell>
                <TableCell>
                  <div
                    className="max-w-[200px] truncate"
                    title={leadData.lastCallOutcome}
                  >
                    {leadData.lastCallOutcome || "No outcome recorded"}
                  </div>
                </TableCell>
                <TableCell>
                  <div
                    className="max-w-[150px] truncate"
                    title={leadData.lastCallEndedReason}
                  >
                    {leadData.lastCallEndedReason || "-"}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleCall(
                          leadData.leadId,
                          leadData.phoneNumber,
                          leadData.name,
                        )
                      }
                      disabled={!leadData.phoneNumber}
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      Call
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSchedule(leadData.leadId)}
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Schedule
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {selectedLeadId && (
        <>
          {selectedLeadId && (
            <ScheduleLeadCallDialog
              isOpen={scheduleDialogOpen}
              onClose={() => setScheduleDialogOpen(false)}
              lead={{
                id: selectedLeadId,
                first_name: selectedLeadName.split(' ')[0] || '',
                last_name: selectedLeadName.split(' ').slice(1).join(' ') || '',
                email: '',
                phone_number: selectedPhoneNumber,
                organization_id: organizationId,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              } as any}
              onScheduled={() => {
                setScheduleDialogOpen(false);
                toast({
                  title: "Call scheduled",
                  description: "The call has been scheduled successfully",
                });
              }}
              mode="schedule"
              campaignId={campaignId}
            />
          )}

          <ConfirmCallDialog
            open={confirmCallDialogOpen}
            onOpenChange={setConfirmCallDialogOpen}
            leadName={selectedLeadName}
            phoneNumber={selectedPhoneNumber}
            agentName={agentName}
            variables={callVariables}
            onConfirm={initiateCall}
          />
        </>
      )}
    </div>
  );
}
